import { ZonedDateTimeFactory, ZonedDateTimeFormatter } from '@csp/csp-common-date-util';
import {
  Compliance,
  ComplianceValue,
  ComplianceMetricItem,
  ComplianceMetricItemType,
  ZonedDateTime,
  Maybe,
} from '@csp/csp-common-model';
import { maxBy } from 'lodash';
import { ComplianceMetricV1, ComplianceV1, CompliantStateType } from '@csp/dmdp-api-user-dto';

const toComplianceValue = (value: number, isCompliant: boolean): ComplianceValue => ({
  value,
  isCompliant,
  notCompliant: !isCompliant,
  percentage: value * 100,
});

const toComplianceMetricItem = ({
  scheduleCode,
  requestCode,
  value,
  isCompliant,
  recentValue,
  isRecentCompliant,
  type,
  updatedAt,
  lastSync,
}: ComplianceMetricV1): ComplianceMetricItem => ({
  scheduleRef: { scheduleCode, requestCodes: requestCode.split('/') },
  value: toComplianceValue(value, isCompliant),
  recentValue:
    recentValue !== undefined && isRecentCompliant !== undefined
      ? toComplianceValue(recentValue, isRecentCompliant)
      : undefined,
  type: type as ComplianceMetricItemType,
  updatedAt: ZonedDateTimeFactory.fromZonedDateTimeString(updatedAt),
  lastSync: lastSync !== undefined ? ZonedDateTimeFactory.fromZonedDateTimeString(lastSync) : undefined,
});

const getLastSync = (metrics: ComplianceMetricItem[]): Maybe<ZonedDateTime> =>
  maxBy(metrics, ({ lastSync }) => lastSync?.unixTimeMillis ?? Number.MIN_SAFE_INTEGER)?.lastSync;

const getLastSyncFromMetricsV1 = (metrics: ComplianceMetricV1[]): Maybe<ZonedDateTime> => {
  const maybeLastSyncStr = maxBy(
    metrics,
    ({ lastSync }) =>
      (lastSync !== undefined && ZonedDateTimeFactory.fromZonedDateTimeString(lastSync)?.unixTimeMillis) ??
      Number.MIN_SAFE_INTEGER,
  )?.lastSync;
  return maybeLastSyncStr !== undefined ? ZonedDateTimeFactory.fromZonedDateTimeString(maybeLastSyncStr) : undefined;
};

const toCompliance = ({ updatedAt, state, recentState, metrics }: ComplianceV1): Compliance => {
  const metricsWithRecentCompliance = metrics.filter(metric => metric.recentValue !== undefined);

  return {
    updatedAt: ZonedDateTimeFactory.fromZonedDateTimeString(updatedAt),
    state,
    recentState,
    metrics: metrics.map(toComplianceMetricItem),
    lastSync: getLastSyncFromMetricsV1(metrics),
    recentLastSync: getLastSyncFromMetricsV1(metricsWithRecentCompliance),
  };
};

const toComplianceMetricV1 = ({
  scheduleRef,
  type,
  value,
  recentValue,
  updatedAt,
  lastSync,
}: ComplianceMetricItem): ComplianceMetricV1 => ({
  requestCode: scheduleRef.requestCodes.join('/'),
  scheduleCode: scheduleRef.scheduleCode,
  type,
  updatedAt: ZonedDateTimeFormatter.toZonedDateTimeString(updatedAt),
  lastSync: ZonedDateTimeFormatter.toZonedDateTimeString(lastSync),
  isCompliant: value.isCompliant,
  value: value.value,
  recentValue: recentValue?.value,
  isRecentCompliant: recentValue?.isCompliant,
});

const toComplianceV1 = ({ state, recentState, updatedAt, metrics }: Compliance): ComplianceV1 => ({
  state,
  recentState,
  timestamp: updatedAt.unixTimeMillis,
  updatedAt: ZonedDateTimeFormatter.toZonedDateTimeString(updatedAt),
  metrics: metrics.map(toComplianceMetricV1),
});

const isTotalNonCompliant = ({ value }: ComplianceMetricItem): boolean => !value.isCompliant;
const toCompliantStateTypeFromMetricItems = (metrics: ComplianceMetricItem[]): CompliantStateType =>
  !metrics.length
    ? CompliantStateType.NO_DATA
    : metrics.find(isTotalNonCompliant)
      ? CompliantStateType.NOT_COMPLIANT
      : CompliantStateType.COMPLIANT;

const isRecentNonCompliant = ({ recentValue }: ComplianceMetricItem): boolean => !recentValue?.isCompliant;
const toCompliantRecentStateTypeFromMetricItems = (metrics: ComplianceMetricItem[]): CompliantStateType =>
  !metrics.length
    ? CompliantStateType.NO_DATA
    : metrics.find(isRecentNonCompliant)
      ? CompliantStateType.NOT_COMPLIANT
      : CompliantStateType.COMPLIANT;

const toComplianceFromMetricItems = (metrics: ComplianceMetricItem[], updatedAt?: ZonedDateTime): Compliance => {
  const metricsWithRecentCompliance = metrics.filter(metric => metric.recentValue);

  return {
    state: toCompliantStateTypeFromMetricItems(metrics),
    recentState: toCompliantRecentStateTypeFromMetricItems(metricsWithRecentCompliance),
    updatedAt: updatedAt ?? ZonedDateTimeFactory.now(),
    lastSync: getLastSync(metrics),
    recentLastSync: getLastSync(metricsWithRecentCompliance),
    metrics,
  };
};

export const ComplianceMapper = {
  toCompliance,
  toComplianceV1,
  toComplianceFromMetricItems,
};
